import { useState, useMemo, useEffect } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { MainWorkOrder, SubWorkOrder } from '@/types';
import { MainWorkOrderForm } from './MainWorkOrderForm';
import { SubWorkOrderForm } from './SubWorkOrderForm';
import { toast } from '@/components/UI/Toast';

export function WorkOrderManagement() {
  const orders = useOrderStore((s) => s.orders);
  const mainWorkOrders = useOrderStore((s) => s.mainWorkOrders);
  const subWorkOrders = useOrderStore((s) => s.subWorkOrders);
  const deleteMainWorkOrder = useOrderStore((s) => s.deleteMainWorkOrder);
  const deleteSubWorkOrder = useOrderStore((s) => s.deleteSubWorkOrder);
  const cascadeDelayFromSubOrder = useOrderStore((s) => s.cascadeDelayFromSubOrder);
  const checkAndUpdateOvertimeStatus = useOrderStore((s) => s.checkAndUpdateOvertimeStatus);
  const persons = useResourceStore((s) => s.persons);
  const machines = useResourceStore((s) => s.machines);

  // 初始掃描逾期狀態
  useEffect(() => {
    checkAndUpdateOvertimeStatus();
  }, []);

  // 篩選狀態
  const [filterOrderId, setFilterOrderId] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // 展開的制令單
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // 表單狀態
  const [showMWOForm, setShowMWOForm] = useState(false);
  const [editingMWO, setEditingMWO] = useState<MainWorkOrder | undefined>();
  const [showSWOForm, setShowSWOForm] = useState(false);
  const [editingSWO, setEditingSWO] = useState<SubWorkOrder | undefined>();
  const [swoParentId, setSwoParentId] = useState('');

  // 延遲輸入
  const [delayTarget, setDelayTarget] = useState<string | null>(null);
  const [delayDays, setDelayDays] = useState(1);

  // 刪除確認
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'mwo' | 'swo'; id: string; name: string } | null>(null);

  // 過濾制令單
  const filteredMWOs = useMemo(() => {
    let list = mainWorkOrders;
    if (filterOrderId !== 'all') {
      list = list.filter((m) => m.orderId === filterOrderId);
    }
    if (filterStatus !== 'all') {
      list = list.filter((m) => m.status === filterStatus);
    }
    return list;
  }, [mainWorkOrders, filterOrderId, filterStatus]);

  // 即時計算逾期狀態（不依賴 store 中的 isOvertime，而是即時比對日期）
  const now = Date.now();
  const isSWOOvertime = (swo: SubWorkOrder) =>
    swo.status !== '已完成' && new Date(swo.plannedEnd).getTime() < now;

  // 判斷制令單是否有逾期的派工單
  const isMWOOvertime = (mwoId: string) =>
    subWorkOrders.some((s) => s.mainOrderId === mwoId && isSWOOvertime(s));

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getOrderLabel = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    return order ? `${orderId} (${order.productName})` : orderId;
  };

  const getPersonName = (id: string) => persons.find((p) => p.id === id)?.name || id;
  const getMachineName = (id: string | null) => {
    if (!id) return '-';
    const m = machines.find((m) => m.id === id);
    return m ? `${m.name}` : id;
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });

  const getStatusBadge = (status: string) => {
    const configs: Record<string, string> = {
      '待處理': 'bg-slate-100 text-slate-700 border-slate-300',
      '進行中': 'bg-amber-100 text-amber-700 border-amber-300',
      '已完成': 'bg-green-100 text-green-700 border-green-300',
      '待開啟': 'bg-slate-100 text-slate-700 border-slate-300',
      '資源不足': 'bg-red-100 text-red-700 border-red-300',
    };
    return configs[status] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const handleAddMWO = () => {
    setEditingMWO(undefined);
    setShowMWOForm(true);
  };

  const handleEditMWO = (mwo: MainWorkOrder) => {
    setEditingMWO(mwo);
    setShowMWOForm(true);
  };

  const handleAddSWO = (mainOrderId: string) => {
    setEditingSWO(undefined);
    setSwoParentId(mainOrderId);
    setShowSWOForm(true);
  };

  const handleEditSWO = (swo: SubWorkOrder) => {
    setEditingSWO(swo);
    setSwoParentId(swo.mainOrderId);
    setShowSWOForm(true);
  };

  const handleConfirmDelay = () => {
    if (!delayTarget || delayDays <= 0) return;
    cascadeDelayFromSubOrder(delayTarget, delayDays);
    toast.success('延遲已套用', `已將後續派工單延遲 ${delayDays} 天`);
    setDelayTarget(null);
    setDelayDays(1);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'mwo') {
      deleteMainWorkOrder(deleteTarget.id);
      toast.success('制令單已刪除', `${deleteTarget.id} 已刪除`);
    } else {
      deleteSubWorkOrder(deleteTarget.id);
      toast.success('派工單已刪除', `${deleteTarget.id} 已刪除`);
    }
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-4">
      {/* 篩選列 */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-slate-700">訂單：</label>
          <select
            value={filterOrderId}
            onChange={(e) => setFilterOrderId(e.target.value)}
            className="px-3 py-1.5 bg-white border-2 border-blue-200 text-blue-700 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-blue-400 shadow-sm"
          >
            <option value="all">全部訂單</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.id} - {o.productName}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-slate-700">狀態：</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 bg-white border-2 border-blue-200 text-blue-700 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-blue-400 shadow-sm"
          >
            <option value="all">全部狀態</option>
            <option value="待處理">待處理</option>
            <option value="進行中">進行中</option>
            <option value="已完成">已完成</option>
          </select>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs font-bold text-blue-700">
            制令單 {filteredMWOs.length} 筆
          </span>
          <button
            onClick={handleAddMWO}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
          >
            + 新增制令單
          </button>
        </div>
      </div>

      {/* 制令單列表 */}
      {filteredMWOs.length === 0 ? (
        <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl p-12 text-center shadow-sm">
          <div className="text-6xl mb-4">&#x1F4C4;</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">沒有制令單資料</h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            {mainWorkOrders.length === 0
              ? '點擊「新增制令單」建立第一筆制令單'
              : '沒有符合篩選條件的制令單'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredMWOs.map((mwo) => {
            const isExpanded = expandedIds.has(mwo.id);
            const overtime = isMWOOvertime(mwo.id);
            const subs = subWorkOrders
              .filter((s) => s.mainOrderId === mwo.id)
              .sort((a, b) => new Date(a.plannedStart).getTime() - new Date(b.plannedStart).getTime());
            const progress = mwo.totalQuantity > 0
              ? Math.round((mwo.completedQuantity / mwo.totalQuantity) * 100)
              : 0;

            return (
              <div
                key={mwo.id}
                className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all ${
                  overtime
                    ? 'border-2 border-red-500 bg-red-50/30'
                    : 'border-2 border-slate-200'
                }`}
              >
                {/* 制令單卡片頭部 */}
                <div
                  className="px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                  onClick={() => toggleExpand(mwo.id)}
                >
                  <span className={`text-lg transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                    &#x25B6;
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm font-bold text-blue-600 font-mono">{mwo.id}</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusBadge(mwo.status)}`}>
                        {mwo.status}
                      </span>
                      {overtime && (
                        <span className="px-2.5 py-1 bg-red-100 text-red-700 border border-red-300 rounded-full text-xs font-bold">
                          &#x26A0; 逾期
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>訂單：{getOrderLabel(mwo.orderId)}</span>
                      <span>數量：{mwo.completedQuantity}/{mwo.totalQuantity}</span>
                      <span>{formatDate(mwo.plannedStart)} ~ {formatDate(mwo.plannedEnd)}</span>
                    </div>
                  </div>

                  {/* 進度條 */}
                  <div className="flex items-center gap-2 w-32">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          progress >= 80 ? 'bg-green-500' : progress >= 40 ? 'bg-blue-500' : 'bg-amber-500'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-slate-700 min-w-[36px]">{progress}%</span>
                  </div>

                  {/* 操作按鈕 */}
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleEditMWO(mwo)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors text-xs"
                      title="編輯制令單"
                    >
                      編輯
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ type: 'mwo', id: mwo.id, name: mwo.id })}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors text-xs"
                      title="刪除制令單"
                    >
                      刪除
                    </button>
                  </div>
                </div>

                {/* 展開的派工單列表 */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50/50">
                    <div className="px-6 py-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-700">
                        派工單（{subs.length} 筆）
                      </span>
                      <button
                        onClick={() => handleAddSWO(mwo.id)}
                        className="px-4 py-1.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-lg transition-all text-xs font-semibold shadow-sm active:scale-95"
                      >
                        + 新增派工單
                      </button>
                    </div>

                    {subs.length === 0 ? (
                      <div className="px-6 pb-4 text-center text-sm text-slate-500">
                        尚無派工單，請點擊「新增派工單」
                      </div>
                    ) : (
                      <div className="px-6 pb-4 space-y-3">
                        {subs.map((swo) => (
                          <div
                            key={swo.id}
                            className={`rounded-xl p-4 transition-all ${
                              isSWOOvertime(swo)
                                ? 'border-2 border-red-500 bg-red-50'
                                : 'border border-slate-200 bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-mono font-bold text-indigo-600">{swo.id}</span>
                                <span className="text-sm font-semibold text-slate-800">
                                  {swo.processName}
                                </span>
                                <span className="text-xs text-slate-500 font-mono">{swo.processCode}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${getStatusBadge(swo.status)}`}>
                                  {swo.status}
                                </span>
                                {isSWOOvertime(swo) && (
                                  <span className="text-red-600 font-bold text-xs">&#x26A0; 逾期</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {delayTarget === swo.id ? (
                                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="number"
                                      min="1"
                                      max="365"
                                      value={delayDays}
                                      onChange={(e) => setDelayDays(Number(e.target.value))}
                                      className="w-16 px-2 py-1 border-2 border-amber-300 rounded-lg text-xs text-center focus:outline-none focus:ring-2 focus:ring-amber-400"
                                      autoFocus
                                    />
                                    <span className="text-xs text-slate-500">天</span>
                                    <button
                                      onClick={handleConfirmDelay}
                                      className="px-2 py-1 bg-amber-500 text-white rounded text-xs font-semibold hover:bg-amber-600"
                                    >
                                      確認
                                    </button>
                                    <button
                                      onClick={() => setDelayTarget(null)}
                                      className="px-2 py-1 bg-slate-200 text-slate-700 rounded text-xs font-semibold hover:bg-slate-300"
                                    >
                                      取消
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    {swo.status !== '已完成' && (
                                      <button
                                        onClick={() => { setDelayTarget(swo.id); setDelayDays(1); }}
                                        className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-300 rounded text-xs font-semibold hover:bg-amber-100 transition-colors"
                                        title="標記延遲並級聯更新後續派工單"
                                      >
                                        標記延遲
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleEditSWO(swo)}
                                      className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs font-semibold transition-colors"
                                    >
                                      編輯
                                    </button>
                                    <button
                                      onClick={() => setDeleteTarget({ type: 'swo', id: swo.id, name: `${swo.processName} (${swo.id})` })}
                                      className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-xs font-semibold transition-colors"
                                    >
                                      刪除
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-600">
                              <div>
                                <span className="text-slate-400">人員：</span>
                                {swo.assignedPersons.length > 0
                                  ? swo.assignedPersons.map(getPersonName).join('、')
                                  : '-'}
                              </div>
                              <div>
                                <span className="text-slate-400">設備：</span>
                                {getMachineName(swo.machineId)}
                              </div>
                              <div>
                                <span className="text-slate-400">數量：</span>
                                {swo.actualQuantity}/{swo.plannedQuantity}
                              </div>
                              <div>
                                <span className="text-slate-400">時間：</span>
                                {formatDate(swo.plannedStart)} ~ {formatDate(swo.plannedEnd)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 制令單表單 */}
      <MainWorkOrderForm
        isOpen={showMWOForm}
        onClose={() => { setShowMWOForm(false); setEditingMWO(undefined); }}
        editingOrder={editingMWO}
      />

      {/* 派工單表單 */}
      <SubWorkOrderForm
        isOpen={showSWOForm}
        onClose={() => { setShowSWOForm(false); setEditingSWO(undefined); }}
        mainOrderId={swoParentId}
        editingOrder={editingSWO}
      />

      {/* 刪除確認對話框 */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white border-2 border-red-200 rounded-2xl p-8 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-800 mb-3">
              確認刪除{deleteTarget.type === 'mwo' ? '制令單' : '派工單'}
            </h3>
            <p className="text-sm text-slate-600 mb-2">
              即將刪除：<span className="font-bold text-red-700">{deleteTarget.name}</span>
            </p>
            {deleteTarget.type === 'mwo' && (
              <p className="text-xs text-red-600 mb-4">
                &#x26A0; 刪除制令單將同時刪除其下所有派工單
              </p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-5 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-all"
              >
                取消
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-5 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg text-sm font-semibold shadow-lg shadow-red-500/30 hover:from-red-700 hover:to-rose-700 transition-all active:scale-95"
              >
                確認刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
